import { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import useIntakeCooldown from "../../hooks/useIntakeCooldown";
import useIntakeUpload from "../../hooks/useIntakeUpload";
import { flattenError } from "../../utils/errorDisplay";
import { intakeErrorText } from "../../utils/intakeLimits";

const ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
];
const ACCEPTED_EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".gif", ".heic", ".heif"];
const MAX_IMAGES = 30;

const Container = styled.div`
  width: 100%;
`;

/** @type {import('styled-components').StyledComponent<'div', any, { $isDragOver?: boolean }, never>} */
const DropZone = styled.div`
  width: 100%;
  border: 2px dashed ${(/** @type {{ $isDragOver?: boolean }} */ props) => (props.$isDragOver ? "#6b7280" : "#d1d5db")};
  border-radius: 12px;
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${(/** @type {{ $isDragOver?: boolean }} */ props) => (props.$isDragOver ? "#f3f4f6" : "#fafafa")};
  font: inherit;

  &:hover {
    border-color: #6b7280;
    background: #f3f4f6;
  }
`;

const DropZoneText = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 8px;
`;

const DropZoneSubtext = styled.p`
  font-size: 13px;
  color: #9ca3af;
  margin: 0;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
  margin-top: 20px;

  @media (max-width: 767px) {
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 8px;
  }
`;

const ImageTile = styled.div`
  position: relative;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background: #e5e7eb;
`;

const TileImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const PlaceholderIcon = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 14px;
  padding: 8px;
  text-align: center;
`;

const DeleteButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 1;
  padding: 0;
  transition: background 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.8);
  }
`;

const Counter = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-top: 12px;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  margin-top: 12px;
  overflow: hidden;
`;

/** @type {import('styled-components').StyledComponent<'div', any, { $percent?: number }, never>} */
const ProgressFill = styled.div`
  height: 100%;
  background: var(--brand-primary, #0e7e7a);
  border-radius: 3px;
  transition: width 0.3s ease;
  width: ${(/** @type {{ $percent?: number }} */ props) => props.$percent}%;
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 14px;
  margin-top: 8px;
`;

/**
 * @param {File} file
 * @returns {boolean}
 */
const isAcceptedFile = (file) => {
  if (ACCEPTED_TYPES.includes(file.type)) return true;
  const ext = `.${file.name.split(".").pop()?.toLowerCase()}`;
  return ACCEPTED_EXTENSIONS.includes(ext);
};

/**
 * Check if a file can be previewed natively in browsers
 * @param {File|{file?: string; url?: string; name?: string}} file
 * @returns {boolean}
 */
const canPreviewNatively = (file) => {
  if (file instanceof File) {
    const ext = file.name.split(".").pop()?.toLowerCase();
    return ext !== "heic" && ext !== "heif";
  }
  return true;
};

/**
 * @typedef {Object} UploadedImage
 * @property {number|string} id
 * @property {string} [file]
 * @property {string} [url]
 */

/**
 * @typedef {Object} ImageUploaderProps
 * @property {UploadedImage[]} images - Already uploaded images from the server
 * @property {(files: File[]) => any} onUpload - Returns the upload result when available
 * @property {(imageId: number|string) => void} onDelete - Called when an image delete is requested
 * @property {boolean} [uploading] - Whether an upload is in progress
 * @property {number} [uploadProgress] - Upload progress 0-100
 * @property {unknown} [error] - Error message to display
 * @property {string} [listingUuid] - Binds a retained selection to its original listing
 */

/** @param {ImageUploaderProps} props */
const ImageUploader = ({
  images,
  onUpload,
  onDelete,
  uploading,
  uploadProgress,
  error,
  listingUuid,
}) => {
  const cooldown = useIntakeCooldown();
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState(/** @type {string|null} */ (null));
  const [localPreviews, setLocalPreviews] = useState(
    /** @type {{file: File, preview: string|null}[]} */ ([])
  );
  const fileInputRef = useRef(/** @type {HTMLInputElement|null} */ (null));

  const totalImages = images.length;
  const remaining = MAX_IMAGES - totalImages;
  const {
    start: startUpload,
    busy: uploadBusy,
    files: retryFiles,
    retry,
    discard,
  } = useIntakeUpload({
    targetKey: listingUuid || "image-upload",
    upload: (files) => {
      const previews = files.map((file) => ({
        file,
        preview: canPreviewNatively(file) ? URL.createObjectURL(file) : null,
      }));
      setLocalPreviews(previews);
      return onUpload(files);
    },
  });
  const isUploading = !!uploading || uploadBusy;
  const displayedError =
    intakeErrorText(error, cooldown.retryAt) ||
    flattenError(error)
      .map(({ message }) => message)
      .join(" ");

  const handleFiles = useCallback(
    (/** @type {File[]} */ files) => {
      if (isUploading || cooldown.blocked) return;
      setValidationError(null);

      const validFiles = files.filter((/** @type {File} */ f) => isAcceptedFile(f));
      if (validFiles.length < files.length) {
        setValidationError(
          "Some files were skipped — only JPEG, PNG, WebP, GIF, HEIC, and HEIF are accepted."
        );
      }

      if (validFiles.length === 0) return;

      if (totalImages + validFiles.length > MAX_IMAGES) {
        setValidationError(
          `You can upload up to ${MAX_IMAGES} photos total. ${remaining} remaining.`
        );
        return;
      }

      void startUpload(validFiles);
    },
    [isUploading, cooldown.blocked, startUpload, totalImages, remaining]
  );

  // Clear the pending previews once an upload attempt SETTLES — succeeded or
  // failed. This used to key off the server image count changing, which a
  // failed upload never does, so the entries survived. They are not visible
  // after a failure (the tiles render only while `uploading`), but they are
  // still there: the next attempt rendered them again beside the new ones, so
  // retrying one photo showed two tiles under one React key, and every object
  // URL from the failed attempt leaked.
  //
  // `uploading` is the right signal because UPLOAD_IMAGES_SUCCESS sets the new
  // listing and clears the loading flag in the same action: by the render where
  // uploading goes false, `images` already holds whatever the server accepted.
  const previewsRef = useRef(localPreviews);
  previewsRef.current = localPreviews;
  const wasUploadingRef = useRef(isUploading);
  useEffect(() => {
    const settled = wasUploadingRef.current && !isUploading;
    wasUploadingRef.current = isUploading;
    if (!settled) return;
    for (const p of previewsRef.current) {
      if (p.preview) URL.revokeObjectURL(p.preview);
    }
    if (previewsRef.current.length > 0) setLocalPreviews([]);
  }, [isUploading]);

  // Previews outlive the component otherwise; a failed upload used to leak
  // every object URL it created.
  useEffect(
    () => () => {
      for (const p of previewsRef.current) {
        if (p.preview) URL.revokeObjectURL(p.preview);
      }
    },
    []
  );

  /** @param {React.DragEvent} e */
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  /** @param {React.DragEvent} e */
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  /** @param {React.ChangeEvent<HTMLInputElement>} e */
  const handleInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    handleFiles(files);
    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Container>
      {remaining > 0 && (
        <DropZone
          as="button"
          type="button"
          $isDragOver={isDragOver}
          disabled={isUploading || cooldown.blocked}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            disabled={isUploading || cooldown.blocked}
            accept={ACCEPTED_EXTENSIONS.join(",")}
            onChange={handleInputChange}
            style={{ display: "none" }}
            data-testid="file-input"
          />
          <DropZoneText>Drag and drop photos here, or click to browse</DropZoneText>
          <DropZoneSubtext>
            JPEG, PNG, WebP, GIF, HEIC, HEIF — up to {MAX_IMAGES} images
          </DropZoneSubtext>
        </DropZone>
      )}

      {isUploading && uploadProgress != null && (
        <ProgressBar>
          <ProgressFill $percent={uploadProgress} />
        </ProgressBar>
      )}

      {(validationError || displayedError) && (
        <ErrorMessage role="alert">{validationError || displayedError}</ErrorMessage>
      )}
      {retryFiles.length > 0 && (
        <div>
          <p>
            {retryFiles.length} selected photo{retryFiles.length === 1 ? " is" : "s are"} kept on
            this page. Leaving or refreshing requires selecting them again.
          </p>
          <button
            type="button"
            disabled={isUploading || cooldown.blocked}
            onClick={() => void retry()}
          >
            Retry selected photos
          </button>
          <button type="button" disabled={isUploading} onClick={discard}>
            Discard selection
          </button>
        </div>
      )}

      <Counter>
        {totalImages} of {MAX_IMAGES} photos uploaded
      </Counter>

      {(images.length > 0 || localPreviews.length > 0) && (
        <ImageGrid>
          {images.map((img) => {
            const src = img.file || img.url;
            return (
              <ImageTile key={img.id}>
                {src ? (
                  <TileImage src={src} alt="Listing" />
                ) : (
                  <PlaceholderIcon>Image</PlaceholderIcon>
                )}
                <DeleteButton
                  onClick={() => onDelete(img.id)}
                  disabled={isUploading}
                  aria-label="Delete image"
                  title="Delete image"
                >
                  ×
                </DeleteButton>
              </ImageTile>
            );
          })}
          {isUploading &&
            localPreviews.map((lp) => (
              <ImageTile key={`pending-${lp.file.name}-${lp.file.size}`} style={{ opacity: 0.5 }}>
                {lp.preview ? (
                  <TileImage src={lp.preview} alt="Uploading" />
                ) : (
                  <PlaceholderIcon>Uploading…</PlaceholderIcon>
                )}
              </ImageTile>
            ))}
        </ImageGrid>
      )}
    </Container>
  );
};

export default ImageUploader;
